import React from "react";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { NotebookPageContentBlock } from "constants/notebooks/notebook-page-content-blocks";
import { BlockRecord } from "models/view-models/block-record";
import { t } from "utilities/localization-utils";
import PageBlockContextMenu from "organisms/page-blocks/page-block-list/page-block-context-menu";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import useModalActions from "utilities/hooks/use-modal-actions";
import PageBlockAddMenu from "organisms/page-blocks/page-block-list/page-block-add-menu";
import { PageBlockContent } from "molecules/page-block-content/page-block-content";
import { AdditionalBlockData } from "models/interfaces/additional-block-data/additional-block-data";

export interface InactivePageBlockProps {
    additionalBlockData: AdditionalBlockData;
    block: BlockRecord;
    disableMoveDown?: boolean;
    disableMoveUp?: boolean;
    draggingBlockId?: number;
    onAddBlockBelow: (type: NotebookPageContentBlock, id?: number) => void;
    onBlockSaved: (updatedBlock: BlockRecord) => void;
    onClickBlock: (block: BlockRecord) => void;
    onDelete: (block: BlockRecord) => void;
    onHoverBlockStart?: (block: BlockRecord) => void;
    onHoverBlockStop?: (block: BlockRecord) => void;
    onMoveDown: (block: BlockRecord) => void;
    onMoveUp: (block: BlockRecord) => void;
}

export const PAGE_BLOCK_CSS_CLASS_NAME = "c-page-block";

const InactivePageBlock = (props: InactivePageBlockProps) => {
    const {
        additionalBlockData,
        block,
        disableMoveDown,
        disableMoveUp,
        draggingBlockId,
        onAddBlockBelow,
        onBlockSaved,
        onClickBlock,
        onDelete,
        onMoveDown,
        onMoveUp,
    } = props;

    const {
        isOpen: isDeleteConfirmationModalOpen,
        handleClose: handleDeleteConfirmationModalClose,
        handleOpen: handleDeleteConfirmationModalOpen,
    } = useModalActions();

    const handleAddBlockBelow = (type: NotebookPageContentBlock) => {
        onAddBlockBelow(type, block.id);
    };

    const cssClassName = `${PAGE_BLOCK_CSS_CLASS_NAME} ${draggingBlockId === block.id && "-dragging"}`;

    return (
        <article className={cssClassName}>
            {/* Button to make block clickable when navigating with a keyboard */}
            <Button
                style={ButtonStyles.Primary}
                cssClassName={`${PAGE_BLOCK_CSS_CLASS_NAME}__accessibleEditButton`}
                onClick={() => onClickBlock(block)}>
                {t("edit")}
            </Button>
            <PageBlockContent
                block={props.block}
                onBlockSaved={onBlockSaved}
                additionalBlockData={additionalBlockData}
            />
            {/* Button to make block clickable. Using separate button for focus state to allow specific styling/behavior for each button */}
            <button
                tabIndex={-1}
                aria-hidden
                className={`${PAGE_BLOCK_CSS_CLASS_NAME}__editButton`}
                onClick={() => onClickBlock(block)}>
                <span className="sr-only">{t("edit")}</span>
            </button>

            <>
                <PageBlockAddMenu
                    cssClassName={`${PAGE_BLOCK_CSS_CLASS_NAME}__addButton`}
                    onAddBlockBelow={handleAddBlockBelow}
                />
                <nav className={`${PAGE_BLOCK_CSS_CLASS_NAME}__contextMenu`}>
                    <PageBlockContextMenu
                        disableMoveUp={disableMoveUp}
                        disableMoveDown={disableMoveDown}
                        onDelete={handleDeleteConfirmationModalOpen}
                        onMoveDown={() => onMoveDown(block)}
                        onMoveUp={() => onMoveUp(block)}
                    />
                </nav>
            </>
            <ConfirmationModal
                isVisible={isDeleteConfirmationModalOpen}
                message={t("confirmationMessage", {
                    action: t("delete").toLowerCase(),
                    item: t("block"),
                })}
                onCancel={handleDeleteConfirmationModalClose}
                onConfirm={() => onDelete(block)}
            />
        </article>
    );
};

export default InactivePageBlock;
